//import MarketWatchRow from "./MarketWatchRow";
import {TableContainer,
Paper,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Typography,
Box,
Button,
Chip,
Alert
} from "@mui/material";
import React, { useEffect, useState, useCallback, Fragment, useContext, memo, useMemo } from 'react';
import MarketWatchRow from "./MarketWatchRow";
import SymbolInfoModal from "./SymbolInfoModal";
import {useSignalRContext} from "../../SignalR/useSignalR";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';

const MarketWatch = memo(({ onSymbolSelect }) => {
    const { tickData, symbolsList, dataSource, isMockMode, mockDataWarning, lastRealDataUpdate, connect, token } = useSignalRContext();
    const [symbolInfoModal, setSymbolInfoModal] = useState({ open: false, symbolName: null, anchorEl: null });
    const [refreshKey, setRefreshKey] = useState(0);

    // Show all symbols that have tick data (no filtering)
    const filteredTickData = React.useMemo(() => {
        return tickData;
    }, [tickData]);
    
    // Data recovery on mount
    useEffect(() => {
        // MarketWatch mounted
    }, []);

    // Listen for symbol changes and refresh Market Watch
    // TASK 2.2: Removed connect() call - SignalR auto-reconnect handles connection
    // Symbol updates come through existing WebSocket connection via "SymbolReport" event
    useEffect(() => {
        const handleSymbolChange = () => {
            setRefreshKey(prev => prev + 1);
            // Removed: connect(token) - unnecessary reconnection
            // SignalR already connected and will receive "SymbolReport" updates automatically
        };

        // Listen for custom events when symbols are added/updated
        window.addEventListener('symbolAdded', handleSymbolChange);
        window.addEventListener('symbolUpdated', handleSymbolChange);
        window.addEventListener('symbolDeleted', handleSymbolChange);
        window.addEventListener('symbolSubscribed', handleSymbolChange);
        window.addEventListener('symbolUnsubscribed', handleSymbolChange);

        return () => {
            window.removeEventListener('symbolAdded', handleSymbolChange);
            window.removeEventListener('symbolUpdated', handleSymbolChange);
            window.removeEventListener('symbolDeleted', handleSymbolChange);
            window.removeEventListener('symbolSubscribed', handleSymbolChange);
            window.removeEventListener('symbolUnsubscribed', handleSymbolChange);
        };
    }, []);

    const handleRefresh = () => {
        // CRITICAL FIX: Removed connect(token) call
        // Just refresh the UI - SignalR connection is already active
        // Calling connect() was creating unnecessary reconnection attempts
        setRefreshKey(prev => prev + 1);
    };

    const handleSymbolInfo = (symbolName, anchorEl) => {
        if (symbolName === null && anchorEl === null) {
            // Close the modal when null values are passed
            setSymbolInfoModal({ open: false, symbolName: null, anchorEl: null });
        } else {
            // Open the modal with the symbol info
            setSymbolInfoModal({ open: true, symbolName, anchorEl });
        }
    };

    const handleCloseSymbolInfo = () => {
        setSymbolInfoModal({ open: false, symbolName: null, anchorEl: null });
    };

    // NEW: Data source indicator
    const getDataSourceInfo = () => {
        if (dataSource === "real") {
            return {
                icon: <CheckCircleIcon />,
                color: "#22c55e",
                text: "LIVE DATA",
                bgColor: "rgba(34, 197, 94, 0.1)"
            };
        } else if (dataSource === "mock") {
            return {
                icon: <WarningIcon />,
                color: "#f59e0b",
                text: "CACHED DATA",
                bgColor: "rgba(245, 158, 11, 0.1)"
            };
        } else {
            return {
                icon: <SignalWifiOffIcon />,
                color: "#ef4444",
                text: "DISCONNECTED",
                bgColor: "rgba(239, 68, 68, 0.1)"
            };
        }
    };

    const dataSourceInfo = getDataSourceInfo();

    return (
        <div style={{ backgroundColor: filteredTickData.length === 0 ? '#181f2c' : 'transparent', height: '100%' }}>
            {/* Market Watch Header with Refresh Button */}
            <Box className="market-watch-header" sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                backgroundColor: 'rgba(6, 182, 212, 0.05)'
            }}>
                <Typography variant="body2" sx={{
                    color: '#06b6d4',
                    fontWeight: 600,
                    fontSize: '12px'
                }}>
                    Market Watch <span className="symbol-count">({filteredTickData.length} symbols)</span>
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleRefresh}
                    sx={{
                        color: '#06b6d4',
                        '&:hover': {
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            color: '#0891b2'
                        }
                    }}
                >
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Box>
            
            <TableContainer
                component={Paper}
                sx={{
                    boxShadow: "none",
                    backgroundColor: 'transparent',
                    height: "calc(100% - 50px)", // Subtract header height
                    overflowX: 'auto', // Enable horizontal scroll
                    overflowY: 'auto', // Enable vertical scroll
                    // Hide scrollbar but keep functionality
                    '&::-webkit-scrollbar': {
                        display: 'none' // Hide scrollbar for Chrome, Safari, Edge
                    },
                    scrollbarWidth: 'none', // Hide scrollbar for Firefox
                    msOverflowStyle: 'none', // Hide scrollbar for IE
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent'
                    },
                    // Responsive styles
                    '& .MuiTable-root': {
                        fontSize: { xs: '10px', sm: '11px', md: '12px' },
                        minWidth: 'max-content' // Allow table to expand for horizontal scroll
                    }
                }}>
                    <Table
                        sx={{
                            borderSpacing: "0px 4px",
                            borderCollapse: "seperate",
                            // Hide any scrollbar on table
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            marginBottom: '20px', // Add bottom margin to table
                            // Responsive table styles
                            '& .MuiTableCell-root': {
                                padding: { xs: '2px 4px', sm: '4px 6px', md: '6px 8px' },
                                fontSize: { xs: '9px', sm: '10px', md: '11px' }
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                fontWeight: { xs: 500, sm: 600, md: 700 }
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow
                                sx={{
                                    "& .MuiTableCell-root": {
                                        padding: "0px 4px",
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                <TableCell className="market-watch-symbol-col" sx={{ 
                                    width: { xs: "40px", sm: "50px", md: "60px" },
                                    minWidth: { xs: "40px", sm: "50px", md: "60px" }
                                }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography color="text.tableheader" sx={{ 
                                            fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                            fontWeight: { xs: 500, sm: 600, md: 700 }
                                        }}>
                                            {"Symbol"}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{
                                    minWidth: { xs: "50px", sm: "55px", md: "60px" },
                                    maxWidth: { xs: "50px", sm: "55px", md: "60px" }
                                }}>
                                    <Typography color="text.tableheader" sx={{
                                        fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                        fontWeight: { xs: 500, sm: 600, md: 700 }
                                    }}>
                                        {"Bid"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{
                                    minWidth: { xs: "50px", sm: "55px", md: "60px" },
                                    maxWidth: { xs: "50px", sm: "55px", md: "60px" }
                                }}>
                                    <Typography color="text.tableheader" sx={{
                                        fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                        fontWeight: { xs: 500, sm: 600, md: 700 }
                                    }}>
                                        {"Ask"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{
                                    minWidth: { xs: "45px", sm: "50px", md: "55px" },
                                    maxWidth: { xs: "45px", sm: "50px", md: "55px" },
                                    display: { xs: 'none', sm: 'table-cell' }
                                }}>
                                    <Typography color="text.tableheader" sx={{
                                        fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                        fontWeight: { xs: 500, sm: 600, md: 700 }
                                    }}>
                                    {"Spread"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{
                                    minWidth: { xs: "40px", sm: "45px", md: "50px" },
                                    maxWidth: { xs: "40px", sm: "45px", md: "50px" },
                                    display: { xs: 'none', sm: 'table-cell' }
                                }}>
                                    <Typography color="text.tableheader" sx={{
                                        fontSize: { xs: '8px', sm: '9px', md: '10px' },
                                        fontWeight: { xs: 500, sm: 600, md: 700 }
                                    }}>
                                    {"Digits"}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{
                            position: "relative",
                            zIndex: 0,
                            '& tr:last-child td': {
                                paddingBottom: '16px' // Add extra padding to last row
                            }
                        }}>
                            {filteredTickData.length > 0 ?(
                                filteredTickData?.map((item) => (
                                    <MarketWatchRow
                                    key={item.symbolName}
                                    symbol={item.symbolName}
                                    bid={item.bid}
                                    ask={item.ask}
                                    digits={item.digits}
                                    isMockData={isMockMode}
                                    onSymbolInfo={handleSymbolInfo}
                                    onSymbolSelect={onSymbolSelect}
                                    ></MarketWatchRow>
                                ))
                           ) : (
                                    <TableRow>
                                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none', background: 'none' }}>
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexGrow: 1,
                                            width: '100%',
                                            background: 'none',
                                            boxShadow: 'none',
                                            border: 'none',
                                            p: 0,
                                            m: 0
                                        }}>
                                            <img src="/laptops-graph.png" alt="No data" style={{ width: 40, height: 40, opacity: 0.7, marginBottom: 10 }} />
                                            <Typography style={{ color: '#7b879d', fontSize: 14, fontWeight: 500, marginBottom: 2, textAlign: 'center' }}>
                                                No market data available
                                            </Typography>
                                            <Typography style={{ color: '#7b879d', fontSize: 11, fontWeight: 400, opacity: 0.7, textAlign: 'center' }}>
                                                Check your connection
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
            </TableContainer>
            
            {/* Symbol Info Modal */}
            <SymbolInfoModal
                open={symbolInfoModal.open}
                onClose={handleCloseSymbolInfo}
                symbolName={symbolInfoModal.symbolName}
                anchorEl={symbolInfoModal.anchorEl}
            />
        </div>
    );
});

export default MarketWatch;